import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/feature/Layout';

const FORM_URL = "https://readdy.ai/api/form/d6b7esdt1k61917cn8s0";

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      // Guard clause – should never happen because of maxLength, but keep it safe
      setErrorMessage('Message exceeds the maximum length of 500 characters.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        // Try to extract a useful message, fallback to generic one
        const errorText = await response.text();
        throw new Error(errorText || `Server responded with ${response.status}`);
      }

      setSubmitted(true);
    } catch (err) {
      // Preserve user‑friendly feedback
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred.';
      console.error('Form submission error:', err);
      setErrorMessage(message);
      // Still show the thank‑you UI to avoid stuck state, but you could also keep the form.
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#FAFAF8]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <p className="text-[11px] tracking-[0.25em] text-gray-400 font-medium mb-3">
            GET IN TOUCH
          </p>
          <h1
            className="text-[36px] lg:text-[48px] font-bold tracking-tight text-gray-900"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}
          >
            <Link to="/contact" className="cursor-pointer">
              Contact
            </Link>
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <div className="mb-10">
                <p className="text-[11px] tracking-[0.2em] text-gray-400 font-medium mb-3">
                  ADDRESS
                </p>
                <p className="text-[14px] leading-[1.8] text-gray-700">
                  OAK BLDG Ⅱ, 1-11-1 Kamimeguro,
                  <br />
                  Meguro-ku, Tokyo, 153-0051, Japan
                </p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] text-gray-400 font-medium mb-3">
                  COMPANY
                </p>
                <p className="text-[14px] text-gray-700">
                  SWELL HOLDINGS Co.,Ltd.
                </p>
              </div>
            </div>

            <div className="lg:col-span-8">
              {submitted ? (
                <div className="bg-[#FAFAF8] rounded-lg p-12 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-900 text-white mx-auto mb-5">
                    {/* Use explicit closing tag for <i> to avoid JSX parsing issues */}
                    <i className="ri-check-line text-[24px]"></i>
                  </div>
                  <h3
                    className="text-[20px] font-semibold text-gray-900 mb-2"
                    style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                  >
                    Thank you
                  </h3>
                  <p className="text-[14px] text-gray-500">
                    Your message has been sent successfully. We will get back
                    to you shortly.
                  </p>
                  {errorMessage && (
                    <p className="mt-4 text-sm text-red-600">{errorMessage}</p>
                  )}
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[12px] tracking-[0.08em] text-gray-500 font-medium mb-2">
                        NAME <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 transition-colors bg-white"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] tracking-[0.08em] text-gray-500 font-medium mb-2">
                        EMAIL <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 transition-colors bg-white"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] tracking-[0.08em] text-gray-500 font-medium mb-2">
                      COMPANY
                    </label>
                    <input
                      type="text"
                      name="company"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 transition-colors bg-white"
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] tracking-[0.08em] text-gray-500 font-medium mb-2">
                      SUBJECT <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="subject"
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Business Partnership">
                        Business Partnership
                      </option>
                      <option value="Press / Media">Press / Media</option>
                      <option value="Careers">Careers</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] tracking-[0.08em] text-gray-500 font-medium mb-2">
                      MESSAGE <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 transition-colors bg-white resize-none"
                      placeholder="Your message (max 500 characters)"
                    />
                  </div>
                  {errorMessage && (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 bg-gray-900 text-white text-[12px] tracking-[0.1em] font-medium rounded-md hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
